<template>
  <v-alert
    v-if="cameraLoginError"
    type="warning"
    elevation="2"
    icon="warning"
    prominent
  >
    {{ cameraLoginError }}
  </v-alert>
  <div
    v-else-if="!isAuthenticated"
    :class="!iFrameLogin ? 'd-flex align-center justify-center' : ''"
  >
    <v-form
      ref="form"
      v-model="valid"
      lazy-validation
      style="width: 100%"
      :class="iFrameLogin ? '' : 'height-300 align-center justify-center'"
    >
      <v-card-text :class="smAndDown ? 'mx-16' :''">
        <template v-if="!iFrameLogin">
          <p class="text-center mb-4" >
            Login to your {{ cameraProviderName }} account to view the security
            cameras
          </p>
        </template>
        <!-- Custom Login (DtIQ) -->
        <v-row v-if="isLoginThroughTheirCrappyPortal" class="pa-3">
          <v-btn color="primary" dark block tile @click="login">
            Login Portal
          </v-btn>
          <p class="error" v-if="error">{{ error }}</p>
        </v-row>
        <div v-else-if="iFrameLogin" style="height: 600px">
          <iframe class="full" :src="iFrameLogin"></iframe>
        </div>
        <v-row v-else align="center" justify="center">
          <v-col>
            <v-text-field
              label="Username"
              density="compact"
              color="primary"
              autocomplete="false"
              :rules="usernameRules"
              :error-messages="error"
              @focus="clearError"
              v-model="username"
              validate-on-blur
              variant="outlined"
              append-inner-icon="mdi-account-box-outline"
            />
            <v-text-field
              label="Password"
              variant="outlined"
              density="compact"
              color="primary"
              autocomplete="false"
              :rules="passwordRules"
              v-model="password"
              validate-on-blur
              :append-inner-icon="
                showPassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'
              "
              @click:append-inner="togglePassword"
              :type="showPassword ? 'text' : 'password'"
            />
            <v-btn
              color="primary"
              dark
              block
              tile
              @click="login"
              :loading="loading"
            >
              Login
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-form>
  </div>
  <div v-else>
    <slot />
  </div>
</template>

<script setup>
import { computed, ref, toRefs, watch } from 'vue';
import { CameraIntegration } from '../helpers/camera/base';
import { ProviderMap } from '../helpers/camera-utils';
import { useDisplay } from 'vuetify';

const props = defineProps({
  camera: {
    type: CameraIntegration,
    required: true,
  },
});
const { camera } = toRefs(props);
const { smAndDown } = useDisplay();

const username = ref('');
const password = ref('');

const usernameRules = ref([
  (v) => !!v || 'Username is required',
  (v) => (v && v.length <= 255) || 'Username must be less than 255 characters',
]);
const passwordRules = ref([(v) => !!v || 'Password is required']);

const valid = ref(true);
const loading = ref(false);
const showPassword = ref(false);
const error = ref('');
const cameraCredentialError = ref(null);

const isAuthenticated = computed(() => camera.value.isAuthenticated);
const iFrameLogin = computed(() => camera.value.iFrameLogin);
const cameraProvider = computed(() => camera.value?.camera?.camProvider);
const cameraProviderName = computed(() => {
  if (cameraProvider.value === 'itech' || cameraProvider.value === 'ITECH') {
    // itech/3xLogic can have many different providers so keep it generic
    return '3xLogic';
  } else {
    return ProviderMap[cameraProvider.value] || cameraProvider.value;
  }
});
const isLoginThroughTheirCrappyPortal = computed(() => camera.value?.isLoginThroughTheirCrappyPortal);
const cameraLoginError = computed(() => cameraCredentialError.value || camera.value?.authError);

const form = ref(null);
async function login() {
  if (!form.value.validate()) return;
  loading.value = true;

  // remove trailing whitespaces on username or password
  username.value = username.value.trim();
  password.value = password.value.trim();

  try {
    const response = await camera.value.submitCredentials(username.value, password.value);
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

function clearError() {
  error.value = '';
}

function togglePassword() {
  showPassword.value = !showPassword.value;
}

watch(camera, () => {
  if (camera.value) {
    try {
      camera.value.checkForCredentials();
    } catch (err) {
      cameraCredentialError.value = err.message;
    }
  }
}, { immediate: true });
</script>

<style scoped lang="scss">
.full {
  width: 100%;
  height: 100%;
}
</style>
